import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { AppInsightSto } from 'entities/app-insight-sto';
import { CartResultLineItem, CartResultLineItemSelection, GroupedCartResultLineItemSelection } from 'entities/cart-result';
import { Cart } from 'entities/cart-search';
import { AddStoCartItemRequest } from 'entities/carts/add-sto-cart-item-request';
import { Permission } from 'entities/enums';
import { InventoryInfo } from 'entities/national-inventory/inventory-info';
import { groupCumminsVinItems } from 'helpers/group-cummins-vin-items';
import { sortData } from 'helpers/sortData';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthorizationService } from 'services/authorization.service';
import { LoggerService, SourceLocationType } from 'services/logger.service';
import { AppState } from 'store/app-state';
import * as BackCounterActions from 'store/back-counter/back-counter.actions';
import { OpeningFrom, RequestOrderItem } from 'store/back-counter/back-counter.entities';
import * as BackCounterSelectors from 'store/back-counter/back-counter.selectors';
import * as BranchSelectors from "store/branch/branch.selectors";
import * as MyCartActions from 'store/my-cart/my-cart.actions';
import * as MyCartSelectors from 'store/my-cart/my-cart.selectors';

type CartResultLineItemSelectionSto = GroupedCartResultLineItemSelection & {
  sortAsc: boolean;
  sortBy: string;
  filteredInventories: InventoryInfo[]
};

@Component({
    selector: 'stock-transfer-order',
    templateUrl: './stock-transfer-order.component.html',
    standalone: false
})
export class StockTransferOrderComponent implements OnInit {

  @Input() openingFrom: OpeningFrom = 'CART';
  @Input() requestOrderItems: RequestOrderItem[];
  @Input() requestBranchCode: string;
  @Input() orderNumber: string;
  @Input() selectedCartItems: CartResultLineItemSelection[] = new Array<CartResultLineItemSelection>();

  filteredInventories: InventoryInfo[];
  partId: string;
  partNumber: string;
  sortBy: string = '';
  sortAsc: boolean = false;
  removeLineItem = false;

  private readonly requestOrderItemsSubject: BehaviorSubject<RequestOrderItem[]> = new BehaviorSubject<RequestOrderItem[]>([]);
  requestOrderItems$ = this.requestOrderItemsSubject.asObservable();


  filterSubject = new BehaviorSubject<string>('');
  filterChangeAction$: Observable<string> = this.filterSubject.asObservable();
  selectedCartItems$: Observable<CartResultLineItemSelection[]> = this.store.select(MyCartSelectors.selectedCartItems);
  inventoryItems$ = this.store.select(BackCounterSelectors.selectInventories);
  filteredCartItems$: Observable<CartResultLineItemSelectionSto[]> = combineLatest([
    this.selectedCartItems$,
    this.filterChangeAction$,
    this.inventoryItems$,
    this.requestOrderItems$
  ]).pipe(
    map(([selectedCartItems, filterString, inventoryItems, requestOrderItems]) => {
      if (this.openingFrom === 'BCT') {
        return requestOrderItems?.map(m => {
          let inventoryInfo: InventoryInfo[] = [];
          if (inventoryItems?.length > 0) {
            inventoryInfo = inventoryItems.find(f => f.partNumber === m.materialNumber)?.inventory?.filter(f => f.partNumber === m.materialNumber);
          }
          return {
            partNumber: m.materialNumber,
            finalPrice: m.itemTotal,
            knownPartPrice: Number(m.cost),
            inventory: inventoryInfo,
            quantity: m.orderQuantity,
            bctId: m._id,
            filteredInventories: inventoryInfo?.map(m => ({ ...m, partsQuantity: 1 }))
          } as CartResultLineItemSelectionSto;
        }).filter((selectedCartItem: CartResultLineItemSelection) => {
          if (filterString === '') return true;
          return (selectedCartItem.partNumber && selectedCartItem.partNumber.toLowerCase().includes(filterString.toLocaleLowerCase())) ||
            (selectedCartItem.corePartNumber && selectedCartItem.corePartNumber.toLowerCase().includes(filterString.toLocaleLowerCase()));
        });
      } else {
        const groupedItems = groupCumminsVinItems(selectedCartItems);
        return groupedItems
          .filter((ci: GroupedCartResultLineItemSelection) => !this.isProcuredCartItem(ci))
          .map((ci: GroupedCartResultLineItemSelection) => {
          return {
            ...ci,
            filteredInventories: ci.inventory ? ci.inventory.map(inv => ({ ...inv, partsQuantity: 1 })) : []
          } as CartResultLineItemSelectionSto
        }).filter((selectedCartItem: CartResultLineItemSelectionSto) => {
          if (filterString === '') {
            return true;
          }
          return (selectedCartItem?.partNumber?.toLowerCase().includes(filterString.toLowerCase())) ||
            (selectedCartItem?.corePartNumber?.toLowerCase().includes(filterString.toLowerCase()));
        })
      }
    })
  );

  filterKey: string = '';

  cartId: string;
  userId: string;
  branchCode: string;
  rewardNumber: string;
  customerName: string;

  permission: any = Permission;
  currencyCode$ = this.store.select(BranchSelectors.selectCurrencyCode);


  constructor(
    private store: Store<AppState>,
    private loggerService: LoggerService,
    public authorizationService: AuthorizationService,
    private activeModal: NgbActiveModal) {
  }

  ngOnInit() {
    if(this.openingFrom === 'BCT') {
      this.store.dispatch(BackCounterActions.getInventory({ branchCode: this.requestBranchCode, items: this.requestOrderItems }));
      this.requestOrderItemsSubject.next(this.requestOrderItems);
    } else {
      this.store.dispatch(MyCartActions.getInventory());
    }
  }

  public closeModal(): void {
    this.activeModal.close();
  }

  filter() {
    this.filterSubject.next(this.filterKey);
  };

  sortDataBy(cartItem: CartResultLineItemSelectionSto, sortBy: string) {
    if (cartItem.sortBy == sortBy) {
      cartItem.sortAsc = !cartItem.sortAsc;
    } else {
      cartItem.sortAsc = true;
    }

    cartItem.sortBy = sortBy;

    cartItem.filteredInventories = sortData(cartItem.filteredInventories, cartItem.sortBy, cartItem.sortAsc);
  }

  addToCart(addToCartItem: InventoryInfo, cartItem: CartResultLineItemSelectionSto) {
    if(this.openingFrom === 'BCT') {
      this.store.dispatch(
        BackCounterActions.updateStoValue({
          materialNumber: addToCartItem.partNumber,
          orderNumber: this.orderNumber,
          stockTransferOrder: addToCartItem.branchCode,
          knownPartPrice: cartItem.knownPartPrice,
          quantity: addToCartItem.partsQuantity,
          id: cartItem.bctId
        })
      );
      if( addToCartItem.partsQuantity >= cartItem.quantity){
        let updated = this.requestOrderItemsSubject.value.filter(f => f.materialNumber !== cartItem.partNumber);
        this.requestOrderItemsSubject.next(updated);
      }
      else{
        cartItem.quantity = cartItem.quantity - addToCartItem.partsQuantity;
      }

    } else {
      this.addItemFromMyCart(addToCartItem, cartItem);
    }
  }

  addItemFromMyCart(addToCartItem: InventoryInfo, cartItem: CartResultLineItemSelectionSto) {
    this.logAddToCartToAppInsights(addToCartItem, cartItem);
    const stoCartItemRequest: AddStoCartItemRequest = {
      addCartItemRequest: {
        binLocation: '',
        partId: cartItem.partId,
        coreOption: 'NOCORER',
        partNumber: cartItem.partNumber,
        quantity: Number(addToCartItem.partsQuantity),
        trackingParameters: '',
        isPriceVerified: cartItem.isPriceVerified,
        verifiedPrice: cartItem.isPriceVerified ? cartItem.verifiedPrice : undefined,
        rebateField: cartItem.rebateField,
        adjustedPrice: cartItem.adjustedPrice,
        sapCoupons: cartItem.sapCoupons
      },
      originalPartCartItemId: cartItem.cartItemId,
      removeItemsFromCartRequest: null,
      vendorCode: addToCartItem.branchCode != null ? addToCartItem.branchCode : "",
      vendorBranchCode: addToCartItem.branchCode != null ? addToCartItem.branchCode : "",
      vendorBranchName: addToCartItem.branchName != null ? addToCartItem.branchName : "",
    };

    if(addToCartItem.partsQuantity >= cartItem.quantity){
      if (!this.shouldPreserveOriginalCartItem(cartItem)) {
        const cumminsVinRequiredUnderlyingItemsToRemove = (cartItem.groupedCartItemIds ?? [cartItem.cartItemId])
          .map(id => ({ cartItemId: id }));
        stoCartItemRequest.removeItemsFromCartRequest = {
          cartId: cartItem.cartId,
          isPickupDeliveryType: false,
          itemsToRemoveFromCart: cumminsVinRequiredUnderlyingItemsToRemove
        };
      }
    }

    this.store.dispatch(MyCartActions.addStoToCart({addStoRequest: stoCartItemRequest, selectedCartItem: cartItem}));
  }

  private isProcuredCartItem(cartItem: GroupedCartResultLineItemSelection): boolean {
    return cartItem.isBuyout || cartItem.isHotFlag || cartItem.isSTO;
  }

  private shouldPreserveOriginalCartItem(cartItem: CartResultLineItemSelectionSto): boolean {
    return cartItem.isSplit && (cartItem.underlyingItems ?? [])
      .some(item => (item.quantityAvailable ?? 0) > 0);
  }

  logAddToCartToAppInsights(addToCartItem: InventoryInfo, cartItem: CartResultLineItem) {
    const addtoCartParameters = Object.assign(new Cart(), {
      cartId: this.cartId,
      userId: this.userId,
      branchCode: this.branchCode,
      rewardNumber: this.rewardNumber,
      partNumber: cartItem.partNumber,
      description: cartItem.description,
      yourPrice: cartItem.yourPrice,
      quantity: addToCartItem.partsQuantity,
      coreOption: 'NOCORER',
      corePrice: cartItem.corePrice,
      corePartNumber: cartItem.corePartNumber,
      partId: cartItem.partId,
      cost: cartItem.cost,
      listPrice: cartItem.listPrice,
      adjustedPrice: cartItem.adjustedPrice,
      rebateField: cartItem.rebateField,
      isPriceVerified: cartItem.isPriceVerified,
      verifiedPrice: cartItem.verifiedPrice,
      priceOverrideReasonId: cartItem.priceOverrideReasonId,
      partNumOnly: cartItem.partNumOnly,
      vmrsCode: cartItem.vmrsCode,
      vmrsDescription: cartItem.vmrsDescription,
      manufacturer: cartItem.manufacturer,
      cateogory: cartItem.cateogory,
      binLocation: cartItem.binLocation,
      deliveryType: '',
      vendorCode: addToCartItem.branchCode,
      VendorBranchCode: addToCartItem.branchCode,
      VendorName: addToCartItem.branchName,
      TrackingParameters: '',
      IsSTO: true,
      QuantityAvailable: addToCartItem.quantity
    });

    let sourceName = 'NationalInventoryComponent_addToCart__STO';
    let metricName: string = this.loggerService.getMetricValue(sourceName);
    let appInsightSTO: AppInsightSto = {
      customerName: this.customerName,
      cartId: this.cartId,
      userId: this.userId,
      branchCode: this.branchCode,
      rewardNumber: this.rewardNumber,
      vendorNumber: addToCartItem.branchCode,
      vendorName: addToCartItem.branchName,
      source: SourceLocationType[SourceLocationType.Cart],
      plMetricName: sourceName
    };
    appInsightSTO.product = this.loggerService.getAppInsightParts(addtoCartParameters, JSON.stringify(appInsightSTO)?.length);
    this.loggerService.trackMetric(metricName, appInsightSTO);
  }

  changeQuantity(event: number, inventory: InventoryInfo) {
    inventory.partsQuantity = event;
  }

  removeCartItem(cartItem: CartResultLineItemSelectionSto) {
    const cumminsVinRequiredItemIdsToDeselect = cartItem.groupedCartItemIds ?? [cartItem.cartItemId];
    for (const cartItemId of cumminsVinRequiredItemIdsToDeselect) {
      this.store.dispatch(MyCartActions.toggleSelection({ cartItemId }));
    }
    // For Back Counter requests, we need to uncheck the part in the underlying form
    // Dispatch an action to notify the table component to uncheck this item
    if (cartItem.bctId) {
        this.requestOrderItems = this.requestOrderItems.filter(f => f._id !== cartItem.bctId);
        this.store.dispatch(BackCounterActions.removePartFromProcurement({
            partId: cartItem.bctId,
            orderNumber: this.orderNumber
        }));
        this.requestOrderItemsSubject.next(this.requestOrderItems);
    }

  }
}
